import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Bell, Heart, MessageCircle, UserPlus, MessageSquare, ThumbsUp, TrendingUp, Newspaper, BellRing } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { RowSkeleton } from "@/components/Skeletons";
import { enablePush, pushPermission } from "@/lib/push";

function timeAgo(iso) {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

const ICONS = {
  like: Heart, message: MessageCircle, follow: UserPlus,
  comment: MessageSquare, reply: MessageSquare, comment_like: ThumbsUp, price: TrendingUp, summary: Newspaper,
};

function PushBanner() {
  const [perm, setPerm] = useState(pushPermission());
  if (perm === "granted" || perm === "unsupported") return null;
  const ask = async () => {
    const r = await enablePush();
    setPerm(r);
    if (r === "granted") toast.success("Alerts enabled on this device");
    else toast.error("Alerts are blocked — allow notifications in your browser settings");
  };
  return (
    <button onClick={ask} data-testid="enable-push" className="goe-card pressable mx-3 mt-3 flex w-[calc(100%-24px)] items-center gap-3 p-3 text-left"
            style={{ borderColor: "rgba(245,158,11,0.4)" }}>
      <BellRing size={20} className="accent shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-bold" style={{ color: "var(--text)" }}>Turn on device alerts</p>
        <p className="text-xs text-dim">Enquiries, replies, price moves over 2% and your daily summary</p>
      </div>
      <span className="text-xs font-bold accent">Enable</span>
    </button>
  );
}

export default function Notifications() {
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data.items,
  });

  useEffect(() => {
    api.post("/notifications/read").then(() => {
      qc.invalidateQueries({ queryKey: ["notif-unread"] });
    }).catch(() => {});
  }, [qc]);

  const items = data || [];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="sticky top-0 z-30 glass flex items-center gap-3 px-4 pb-3 pt-4">
        <button onClick={() => nav(-1)} data-testid="notif-back" className="pressable glass flex h-10 w-10 items-center justify-center rounded-full">
          <ArrowLeft size={18} />
        </button>
        <h1 className="font-display text-2xl font-extrabold">Notifications</h1>
      </div>
      <PushBanner />

      {isLoading ? (
        <div className="space-y-2 p-3">{[0, 1, 2].map((i) => <div key={i} className="goe-card"><RowSkeleton /></div>)}</div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center px-4 py-24 text-center text-dim">
          <Bell size={46} className="mb-4 opacity-40" />
          <p className="font-display text-lg font-bold" style={{ color: "var(--text)" }}>No notifications yet</p>
          <p className="mt-1 text-sm">Likes, messages, comments and follows will appear here</p>
        </div>
      ) : (
        <div className="space-y-2 p-3">
          {items.map((n, i) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <motion.button key={n.id} initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                             whileTap={{ scale: 0.98 }} onClick={() => n.link && nav(n.link)} data-testid={`notif-${n.id}`}
                             className="goe-card flex w-full items-center gap-3 p-3 text-left"
                             style={{ borderColor: n.read ? "var(--border)" : "var(--accent)" }}>
                <div className="relative">
                  <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-accent text-white" style={{ minWidth: 44 }}>
                    {n.actor?.avatar ? <img src={fileUrl(n.actor.avatar)} alt="" className="h-full w-full object-cover" />
                      : n.actor ? <span className="font-bold">{(n.actor.name?.[0] || "?").toUpperCase()}</span> : <Icon size={18} />}
                  </div>
                  <span className="absolute -bottom-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full text-white" style={{ background: "var(--accent)" }}>
                    <Icon size={13} />
                  </span>
                </div>
                <div className="flex-1">
                  <p className="text-sm" style={{ color: "var(--text)" }}>
                    {n.actor ? <><b>{n.actor.name}</b> {n.text}</> : n.text}
                  </p>
                  <span className="text-xs text-dim">{timeAgo(n.created_at)}</span>
                </div>
                {!n.read && <span className="h-2.5 w-2.5 rounded-full" style={{ background: "var(--accent)" }} />}
              </motion.button>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
